import {
  ActionIcon,
  ActionIconProps,
  createPolymorphicComponent,
  Tooltip,
  TooltipProps,
} from '@mantine/core';
import React from 'react';

export type IBasicActionIconProps = Omit<ActionIconProps, 'component'> & {
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  type?: 'button' | 'submit' | 'reset';
  children?: React.ReactNode;
  label?: string;
  /** Escape hatch for non-default Tooltip config (e.g. position); ignored when label is omitted. */
  tooltipProps?: Omit<TooltipProps, 'label' | 'children'>;
};

const DefaultProps: Partial<IBasicActionIconProps> = {
  variant: 'light',
};

const BasicActionIconBase = React.forwardRef<
  HTMLButtonElement,
  IBasicActionIconProps
>(({ children, label, tooltipProps, type = 'button', ...props }, ref) => {
  const icon = (
    <ActionIcon
      {...DefaultProps}
      {...props}
      ref={ref}
      type={type}
      // An explicitly-passed aria-label (e.g. DisclosurePopover injecting one
      // onto its trigger via cloneElement) must win over the tooltip label —
      // otherwise the derived default silently discards the caller's intent.
      aria-label={props['aria-label'] ?? label}
      disabled={props.disabled || props.loading}
    >
      {children}
    </ActionIcon>
  );
  return label ? (
    <Tooltip label={label} {...tooltipProps}>
      {icon}
    </Tooltip>
  ) : (
    icon
  );
});
BasicActionIconBase.displayName = 'BasicActionIcon';

/** @registry Base Mantine ActionIcon wrapper with optional tooltip label (also exposed as the button's accessible name unless an explicit aria-label is passed) and disabled-while-loading behavior; extend for new icon buttons instead of using ActionIcon directly. */
export const BasicActionIcon = createPolymorphicComponent<
  'button',
  IBasicActionIconProps
>(BasicActionIconBase);
